// src/context/DisclaimerContext.jsx
//
// Fetches the disclaimer once on app load and makes it available
// to any component via useDisclaimer().
//
// Usage in a screen:
//   const { showDisclaimer, disclaimerContent } = useDisclaimer();
//   if (showDisclaimer('notifications')) { /* render banner */ }

import React, { createContext, useContext, useState, useEffect } from "react";
import api from "../lib/api.ts";

const DisclaimerContext = createContext({
  isEnabled: false,
  content: "",
  screens: {},
  isLoading: true,
  showDisclaimer: () => false,
  refresh: () => {},
});

export const DisclaimerProvider = ({ children }) => {
  const [disclaimer, setDisclaimer] = useState({
    isEnabled: false,
    content: "",
    screens: {},
  });
  const [isLoading, setIsLoading] = useState(true);

  const fetchDisclaimer = async () => {
    try {
      // Detect language from localStorage or default to "en"
      const stored = localStorage.getItem("language") || "en";
      const lang = stored.toLowerCase();

      const res = await api.get(`/disclaimer?lang=${lang}`);
      if (res.data) {
        setDisclaimer(res.data);
      }
    } catch (err) {
      // Silently fail — disclaimer is non-critical
      console.warn("Could not load disclaimer:", err?.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDisclaimer();
  }, []);

  /**
   * showDisclaimer(screenKey)
   *
   * Returns true if the disclaimer should be shown on a given screen.
   *
   * screenKey must be one of:
   *   'notifications' | 'sellerVehicles' | 'buyerVehicles' |
   *   'auctions' | 'auctionDetail' | 'dashboard' | 'profile' |
   *   'payments' | 'gatePass' | 'invoices'
   */
  const showDisclaimer = (screenKey) => {
    if (!disclaimer.isEnabled || !disclaimer.content) return false;
    // If the screen key is not in the settings, default to showing it
    if (disclaimer.screens[screenKey] === undefined) return true;
    return !!disclaimer.screens[screenKey];
  };

  return (
    <DisclaimerContext.Provider
      value={{
        isEnabled: disclaimer.isEnabled,
        content: disclaimer.content,
        screens: disclaimer.screens,
        isLoading,
        showDisclaimer,
        refresh: fetchDisclaimer,
      }}
    >
      {children}
    </DisclaimerContext.Provider>
  );
};

export const useDisclaimer = () => useContext(DisclaimerContext);
